import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, DatePicker, Input, Select, Space, Table, Tag, Typography } from 'antd';
import { message } from '../../utils/antd-message';
import type { ColumnsType } from "antd/es/table";
import dayjs, { type Dayjs } from "dayjs";
import {
  fundExceptionsApi,
  type FundExceptionItem,
  type FundExceptionListQuery,
  type FundExceptionStatus,
  type FundExceptionType,
} from "../../api/fund-exceptions.api";
import { useHasPermission } from "../../auth/HasPermission";
import { useGlobalCampusScope } from "../../auth/GlobalCampusContext";
import { studentsApi, type StudentListItem } from "../../api/students.api";
import { MoneyDisplay } from "../../components/MoneyDisplay";
import { FundExceptionDetailDrawer } from "./FundExceptionDetailDrawer";
import {
  entitlementStatusLabel,
  EXCEPTION_TYPE_LABEL,
  exceptionTypeLabel,
  formatDateTime,
  localStatusLabel,
  STATUS_META,
} from "./fund-exception-labels";

const { RangePicker } = DatePicker;
const { Title } = Typography;

/** ADM-14 资金异常：差错单筛选、只读列表与详情入口。 */
export function FundExceptionPage() {
  const canResolve = useHasPermission("ADM-14", "EDIT");
  // 全局校区范围：'ALL' 不携带 campusId（锁定模式，页内无校区筛选，顶栏为唯一口径入口）
  const { scope } = useGlobalCampusScope();
  const [rows, setRows] = useState<FundExceptionItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filters, setFilters] = useState<FundExceptionListQuery>({});
  const [students, setStudents] = useState<StudentListItem[]>([]);
  const [studentSearching, setStudentSearching] = useState(false);
  // 已选学生置顶留存：远程联想刷新选项后防已选筛选显示裸 UUID（LedgerPage 同款回显口径）
  const [pickedStudentOption, setPickedStudentOption] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const studentSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [activeExceptionId, setActiveExceptionId] = useState<string | null>(
    null,
  );

  // 学生筛选远程联想（BS-018：不再整表拉前 100 条静默截断，服务端按姓名/学号/手机号过滤，300ms 防抖）
  const handleStudentSearch = (keyword: string) => {
    if (studentSearchTimer.current) clearTimeout(studentSearchTimer.current);
    if (!keyword.trim()) {
      setStudents([]);
      return;
    }
    studentSearchTimer.current = setTimeout(() => {
      void (async () => {
        setStudentSearching(true);
        try {
          const result = await studentsApi.listStudents({
            keyword: keyword.trim(),
            // O-28：学生联想随全局校区（全部校区不带参）
            campus: scope === 'ALL' ? undefined : scope,
            page: 1,
            pageSize: 20,
          });
          setStudents(result.items);
        } catch {
          setStudents([]);
        } finally {
          setStudentSearching(false);
        }
      })();
    }, 300);
  };

  useEffect(
    () => () => {
      if (studentSearchTimer.current) clearTimeout(studentSearchTimer.current);
    },
    [],
  );

  // 已选项置顶，防远程选项刷新后选中项显示裸 UUID（LedgerPage 同款）
  const studentOptions = useMemo(() => {
    const mapped = students.map((student) => ({
      value: student.id,
      label: `${student.name}（${student.studentNo}）`,
    }));
    if (!pickedStudentOption) return mapped;
    return [
      pickedStudentOption,
      ...mapped.filter((option) => option.value !== pickedStudentOption.value),
    ];
  }, [students, pickedStudentOption]);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fundExceptionsApi.listFundExceptions({
        ...filters,
        campusId: scope === 'ALL' ? undefined : scope,
        page,
        pageSize,
      });
      setRows(result.items);
      setTotal(result.total);
    } catch (error) {
      message.error(
        error instanceof Error ? error.message : "资金异常列表加载失败",
      );
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize, scope]);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  const updateFilters = (patch: Partial<FundExceptionListQuery>) => {
    setFilters((current) => ({ ...current, ...patch }));
    setPage(1);
  };

  const dateRange: [Dayjs, Dayjs] | null =
    filters.discoveredFrom && filters.discoveredTo
      ? [dayjs(filters.discoveredFrom), dayjs(filters.discoveredTo)]
      : null;

  const columns = useMemo<ColumnsType<FundExceptionItem>>(
    () => [
      {
        title: "异常编号",
        dataIndex: "exceptionNo",
        width: 160,
        fixed: "left",
        render: (value: string, record) => (
          <Button
            type="link"
            size="small"
            onClick={() => setActiveExceptionId(record.exceptionId)}
            data-testid={`fund-exception-link-${record.exceptionId}`}
          >
            {value}
          </Button>
        ),
      },
      {
        title: "类型",
        dataIndex: "exceptionType",
        width: 120,
        render: (value: FundExceptionType) => exceptionTypeLabel(value),
      },
      {
        title: "订单号",
        dataIndex: "orderNo",
        width: 150,
        render: (value) => value ?? "-",
      },
      {
        title: "学生",
        dataIndex: "studentName",
        width: 100,
        render: (value) => value ?? "-",
      },
      {
        title: "渠道支付状态",
        dataIndex: "channelStatus",
        width: 120,
        render: (value: string) => localStatusLabel(value),
      },
      {
        title: "本地支付状态",
        dataIndex: "localStatus",
        width: 120,
        render: (value: string) => localStatusLabel(value),
      },
      {
        title: "课时开通状态",
        key: "entitlementStatus",
        width: 120,
        render: (_, record) => entitlementStatusLabel(record),
      },
      {
        title: "异常金额",
        dataIndex: "differenceAmountMinor",
        width: 110,
        render: (value: number) => <MoneyDisplay amountInCents={value} />,
      },
      {
        title: "发现时间",
        dataIndex: "createdAt",
        width: 150,
        render: (value: string) => formatDateTime(value),
      },
      {
        title: "处理状态",
        dataIndex: "status",
        width: canResolve ? 110 : 170,
        fixed: "right",
        render: (value: FundExceptionStatus) => (
          <Space size={4} wrap>
            <Tag color={STATUS_META[value]?.color}>
              {STATUS_META[value]?.label ?? value}
            </Tag>
            {!canResolve && (
              <Tag data-testid="fund-exception-no-permission">无权限</Tag>
            )}
          </Space>
        ),
      },
    ],
    [canResolve],
  );

  return (
    <div>
      <div className="page-header-row">
        <div>
          <Title level={3}>资金异常</Title>
          <p className="page-header-desc">
            核对渠道与本地支付证据，并按审计留痕处理资金差错。
          </p>
        </div>
      </div>
      <Card className="page-filter-card">
        <Space wrap size={12}>
          <Select
            allowClear
            placeholder="异常类型"
            aria-label="异常类型"
            style={{ width: 150 }}
            value={filters.exceptionType}
            options={(
              Object.keys(EXCEPTION_TYPE_LABEL) as FundExceptionType[]
            ).map((value) => ({
              value,
              label: EXCEPTION_TYPE_LABEL[value],
            }))}
            onChange={(exceptionType) => updateFilters({ exceptionType })}
          />
          <Input
            allowClear
            placeholder="订单号"
            aria-label="订单号"
            style={{ width: 170 }}
            value={filters.orderNo}
            onChange={(event) => updateFilters({ orderNo: event.target.value })}
          />
          <Select
            allowClear
            showSearch
            filterOption={false}
            onSearch={handleStudentSearch}
            loading={studentSearching}
            notFoundContent="输入姓名/学号/手机号联想"
            placeholder="学生"
            aria-label="学生"
            style={{ width: 170 }}
            value={filters.studentId}
            options={studentOptions}
            onChange={(studentId) => {
              updateFilters({ studentId });
              setPickedStudentOption(
                studentId
                  ? studentOptions.find((option) => option.value === studentId) ?? null
                  : null,
              );
            }}
          />
          <Select
            allowClear
            placeholder="处理状态"
            aria-label="处理状态"
            style={{ width: 130 }}
            value={filters.status}
            options={Object.entries(STATUS_META).map(([value, meta]) => ({
              value,
              label: meta.label,
            }))}
            onChange={(status) => updateFilters({ status })}
          />
          <RangePicker
            value={dateRange}
            placeholder={["发现开始时间", "发现结束时间"]}
            onChange={(value) =>
              updateFilters({
                discoveredFrom: value?.[0]?.toISOString(),
                discoveredTo: value?.[1]?.toISOString(),
              })
            }
          />
          <Button
            onClick={() => {
              setFilters({});
              setPage(1);
              setPickedStudentOption(null);
            }}
          >
            重置
          </Button>
        </Space>
      </Card>
      <Card className="page-content-card">
        <Table
          rowKey="exceptionId"
          loading={loading}
          columns={columns}
          dataSource={rows}
          scroll={{ x: 1260 }}
          data-testid="fund-exception-table"
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: (count) => `共 ${count} 条`,
            onChange: (nextPage, nextPageSize) => {
              setPage(nextPage);
              setPageSize(nextPageSize);
            },
          }}
        />
      </Card>
      {activeExceptionId && (
        <FundExceptionDetailDrawer
          exceptionId={activeExceptionId}
          canResolve={canResolve}
          onClose={() => setActiveExceptionId(null)}
          onChanged={loadList}
        />
      )}
    </div>
  );
}
